/**
 * UR Brand System — Phase 2 intake configuration.
 *
 * Config-driven questions grouped into LOGICAL STEPS. Each step is one
 * customer-facing screen; a step may contain multiple related questions.
 * The engine (IntakeEngine.jsx) is dumb — all content, grouping and
 * visibility rules live here so copy/options/conditions can change
 * without touching JSX.
 *
 * Customer journey (~19 steps):
 *   BUSINESS: 01 Welcome · 02 Business State · 03 Contact · 04 Business
 *             Basics · (+existing_brand conditional step) · 05 What You
 *             Sell · 06 How Customers Buy · 07 Customers · 08 Local /
 *             Visitors · 09 Why Customers Choose You + Proof ·
 *             10 Competition · 11 Price Reality
 *   BRAND:    12 Perception · 13 Languages · 14 Applications ·
 *             15 Things to Avoid · 16 Future Growth
 *   PROJECT:  17 Timing + Decision Maker · 18 Review · 19 Submit
 */

// Shared option sets --------------------------------------------------------

export const BUSINESS_STAGE_OPTIONS = [
  { value: "startup", label: "Starting a new business", help: "Not yet operating" },
  { value: "operating", label: "Already operating", help: "You have real customers today" },
];

export const EXISTING_IDENTITY_OPTIONS = [
  { value: true, label: "Yes, I already have a logo / brand" },
  { value: false, label: "No, there is no brand identity yet" },
];

export const CUSTOMER_TYPE_OPTIONS = [
  { value: "b2c", label: "Individuals / Consumers (B2C)" },
  { value: "b2b", label: "Other businesses (B2B)" },
  { value: "both", label: "Both" },
];

export const LOCAL_TOURIST_OPTIONS = [
  { value: "local", label: "Mostly locals" },
  { value: "tourist", label: "Mostly visitors / tourists" },
  { value: "both", label: "A real mix of both" },
];

export const OFFER_TYPE_OPTIONS = [
  { value: "product", label: "Products" },
  { value: "service", label: "Services" },
  { value: "both", label: "Both" },
];

export const NAMING_STATUS_OPTIONS = [
  { value: "name_confirmed", label: "I have a confirmed business name" },
  { value: "name_uncertain", label: "I have an idea but I'm not fully sure" },
  { value: "name_needed", label: "I still need to decide on a name" },
];

export const RELATIVE_PRICE_OPTIONS = [
  { value: "lower", label: "Lower than competitors" },
  { value: "similar", label: "Similar to competitors" },
  { value: "higher", label: "Higher than competitors" },
];

export const EXISTING_PREFERENCE_OPTIONS = [
  { value: "attached", label: "I am attached to the current identity" },
  { value: "open", label: "I am open to changing it" },
  { value: "replace", label: "I am ready for something completely new" },
  { value: "ur_recommend", label: "I would like Ur Designs to recommend" },
];

export const EXISTING_INSTALLATIONS_OPTIONS = [
  "signage", "vehicles", "uniforms", "packaging",
  "printed_materials", "website", "social", "other",
].map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

export const PRIMARY_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "nl", label: "Dutch" },
  { value: "pap", label: "Papiamento" },
  { value: "other", label: "Other" },
];

export const CUSTOMER_LANGUAGES_OPTIONS = PRIMARY_LANGUAGE_OPTIONS;

export const APPLICATIONS_OPTIONS = [
  "website", "social_media", "whatsapp", "signage", "vehicle",
  "uniform", "packaging", "print", "presentation", "advertising", "other",
].map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

export const FUTURE_GROWTH_OPTIONS = [
  { value: "new_products_services", label: "New products or services" },
  { value: "new_locations", label: "New locations" },
  { value: "new_islands_countries", label: "New islands / countries" },
  { value: "new_customer_groups", label: "New customer groups" },
  { value: "stay_focused", label: "Stay focused on what we do" },
];

export const DEADLINE_TYPE_OPTIONS = [
  { value: "fixed", label: "There is a fixed launch date" },
  { value: "soon", label: "As soon as possible" },
  { value: "flexible", label: "Flexible" },
];

export const PREFERRED_CONTACT_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "either", label: "Either is fine" },
];

export const DIFFERENTIATION_SELECTION_OPTIONS = [
  { value: "quality", label: "Quality" },
  { value: "speed", label: "Speed / convenience" },
  { value: "expertise", label: "Expertise or specialty" },
  { value: "service", label: "Customer service" },
  { value: "selection", label: "Selection / variety" },
  { value: "location", label: "Location" },
  { value: "trust", label: "Trust / reputation" },
  { value: "innovation", label: "Innovation / something new" },
];

// ---------------------------------------------------------------------------
// Sections (UX grouping only — never replaces the 12 backend groups)
// ---------------------------------------------------------------------------

export const SECTIONS = [
  { id: "business", title: "Business" },
  { id: "brand", title: "Brand" },
  { id: "project", title: "Project" },
];

// ---------------------------------------------------------------------------
// Logical steps — one customer-facing screen each, in journey order.
// Conditional steps carry their own `when` predicate; a step is only
// part of the journey while at least one of its questions is visible.
// ---------------------------------------------------------------------------

export const STEPS = [
  { id: "welcome", section: "business", title: "Welcome" },
  { id: "business_state", section: "business", title: "Business State" },
  { id: "contact", section: "business", title: "Contact & Save Progress" },
  { id: "business_basics", section: "business", title: "Business Basics" },
  {
    id: "existing_brand", section: "business", title: "Your Current Brand",
    help: "Because you already have an identity, a few extra questions help us respect what's working.",
  },
  { id: "what_you_sell", section: "business", title: "What You Sell" },
  { id: "how_customers_buy", section: "business", title: "How Customers Buy" },
  { id: "customers", section: "business", title: "Customers" },
  { id: "local_visitors", section: "business", title: "Local / Visitors" },
  { id: "why_customers_choose", section: "business", title: "Why Customers Choose You" },
  { id: "competition", section: "business", title: "Competition" },
  { id: "price_reality", section: "business", title: "Price Reality" },
  {
    id: "perception", section: "brand", title: "How should customers see you?",
    help: "Six quick scales. Balanced is a real answer.",
  },
  { id: "languages", section: "brand", title: "Languages" },
  { id: "applications", section: "brand", title: "Where Your Brand Will Live" },
  {
    id: "things_to_avoid", section: "brand", title: "Things to Avoid",
    help: "Anything we must respect, avoid, or definitely include.",
  },
  { id: "future_growth", section: "brand", title: "Future Growth" },
  { id: "timing_decision", section: "project", title: "Timing + Decision Maker" },
  { id: "review", section: "project", title: "Review What You Told Us" },
  { id: "submit", section: "project", title: "Submit / Next Steps" },
];

// Helpers used inside `when` predicates.
const g = (project, group, field) => {
  const leaf = project?.brand_data?.[group]?.[field];
  if (!leaf) return undefined;
  return leaf.value;
};

const EXISTING_BRAND_WHEN = (p) =>
  p.business_stage === "operating" && p.existing_identity === true;

// ---------------------------------------------------------------------------
// Questions — each belongs to exactly one logical step (`step`).
// `path` is a project-level key (business_stage, naming_status,
// existing_identity) or `brand_data.<group>.<field>`.
// ---------------------------------------------------------------------------

export const QUESTIONS = [
  // 01 — Welcome
  {
    id: "welcome", step: "welcome", section: "business", type: "informational",
    title: "Let's design your brand together.",
    help: "A short, structured conversation. 10-15 minutes. You can save and come back any time.",
    continueLabel: "Start",
  },

  // 02 — Business State
  {
    id: "business_state", step: "business_state", section: "business",
    type: "single_select",
    path: "business_stage", required: true,
    title: "Where is your business today?",
    options: BUSINESS_STAGE_OPTIONS,
  },
  {
    id: "existing_identity", step: "business_state", section: "business",
    type: "single_select",
    path: "existing_identity", required: true,
    title: "Do you already have a brand identity (logo, name, materials)?",
    options: EXISTING_IDENTITY_OPTIONS,
    when: (p) => p.business_stage === "operating",
  },

  // 03 — Contact / Save Progress
  {
    id: "contact_name", step: "contact", section: "business", type: "short_text",
    path: "brand_data.project.contact_name", required: true,
    title: "What's your name?",
    help: "So we know who's leading this project.",
  },
  {
    id: "contact_email", step: "contact", section: "business", type: "short_text",
    path: "brand_data.project.contact_email",
    title: "Your email", help: "For progress recovery and later communication.",
    inputType: "email",
  },
  {
    id: "contact_whatsapp", step: "contact", section: "business", type: "short_text",
    path: "brand_data.project.contact_whatsapp",
    title: "Your WhatsApp number", help: "Email or WhatsApp — at least one so we can reach you.",
  },
  {
    id: "preferred_contact", step: "contact", section: "business", type: "single_select",
    path: "brand_data.project.preferred_contact",
    title: "Preferred way to reach you?",
    options: PREFERRED_CONTACT_OPTIONS,
    allowUnknown: true,
  },

  // 04 — Business Basics
  {
    id: "business_name", step: "business_basics", section: "business", type: "short_text",
    path: "brand_data.business.business_name",
    title: "Business name",
    help: "If you don't have one yet, leave this empty and we'll cover naming below.",
    allowUnknown: true,
  },
  {
    id: "naming_status", step: "business_basics", section: "business", type: "single_select",
    path: "naming_status", required: true,
    title: "What's the status of your business name?",
    options: NAMING_STATUS_OPTIONS,
  },
  {
    id: "business_descriptor", step: "business_basics", section: "business", type: "short_text",
    path: "brand_data.business.business_descriptor",
    title: "In one sentence, what does your business do?",
    allowUnknown: true,
  },
  {
    id: "business_base", step: "business_basics", section: "business", type: "short_text",
    path: "brand_data.business.business_base",
    title: "Where is your business based?",
    allowUnknown: true,
  },
  {
    id: "operating_markets", step: "business_basics", section: "business", type: "multi_select_text",
    path: "brand_data.business.operating_markets",
    title: "Which markets do you operate in?",
    help: "Press Enter to add each one.",
    allowUnknown: true,
  },

  // 04b — Existing-brand branch (conditional step)
  {
    id: "rebrand_reason", step: "existing_brand", section: "business", type: "long_text",
    path: "brand_data.business.rebrand_reason",
    title: "What's prompting you to rework the brand?",
    allowUnknown: true,
    when: EXISTING_BRAND_WHEN,
  },
  {
    id: "recognized_brand_equity", step: "existing_brand", section: "business", type: "long_text",
    path: "brand_data.business.recognized_brand_equity",
    title: "What do customers already recognize or value about your brand today?",
    allowUnknown: true,
    when: EXISTING_BRAND_WHEN,
  },
  {
    id: "existing_brand_preference", step: "existing_brand", section: "business", type: "single_select",
    path: "brand_data.business.existing_brand_preference",
    title: "How attached are you to the current identity?",
    options: EXISTING_PREFERENCE_OPTIONS,
    allowUnknown: true,
    when: EXISTING_BRAND_WHEN,
  },
  {
    id: "existing_brand_installations", step: "existing_brand", section: "business", type: "multi_select",
    path: "brand_data.business.existing_brand_installations",
    title: "Where does the current brand live today?",
    options: EXISTING_INSTALLATIONS_OPTIONS,
    allowUnknown: true,
    when: EXISTING_BRAND_WHEN,
  },

  // 05 — What You Sell
  {
    id: "offer_type", step: "what_you_sell", section: "business", type: "single_select",
    path: "brand_data.offer.offer_type",
    title: "What do you sell?",
    options: OFFER_TYPE_OPTIONS,
    allowUnknown: true,
  },
  {
    id: "offer_description", step: "what_you_sell", section: "business", type: "long_text",
    path: "brand_data.offer.offer_description", required: true,
    title: "Describe what you offer, in plain words.",
  },

  // 06 — How Customers Buy
  {
    id: "purchase_channels", step: "how_customers_buy", section: "business", type: "multi_select_text",
    path: "brand_data.offer.purchase_channels",
    title: "How do customers buy from you?",
    help: "Website, in person, WhatsApp, retailers, marketplaces, etc.",
    allowUnknown: true,
  },

  // 07 — Customers
  {
    id: "customer_type", step: "customers", section: "business", type: "single_select",
    path: "brand_data.customer.customer_type", required: true,
    title: "Who are your customers?",
    options: CUSTOMER_TYPE_OPTIONS,
  },
  {
    id: "current_primary_customer", step: "customers", section: "business", type: "long_text",
    path: "brand_data.customer.current_primary_customer",
    title: "Describe your current primary customer.",
    allowUnknown: true,
    when: (p) => p.business_stage === "operating",
  },
  {
    id: "expected_primary_customer", step: "customers", section: "business", type: "long_text",
    path: "brand_data.customer.expected_primary_customer",
    title: "Who do you expect your first customers to be?",
    allowUnknown: true,
    when: (p) => p.business_stage === "startup",
  },
  {
    id: "desired_primary_customer", step: "customers", section: "business", type: "long_text",
    path: "brand_data.customer.desired_primary_customer",
    title: "Who is the customer you most want to attract?",
    allowUnknown: true,
  },
  {
    id: "buyer", step: "customers", section: "business", type: "short_text",
    path: "brand_data.customer.buyer",
    title: "In your B2B sales, who is the BUYER (the one who signs off)?",
    allowUnknown: true,
    when: (p) => ["b2b", "both"].includes(g(p, "customer", "customer_type")),
  },
  {
    id: "user", step: "customers", section: "business", type: "short_text",
    path: "brand_data.customer.user",
    title: "And who is the USER (the one who actually uses it day-to-day)?",
    allowUnknown: true,
    when: (p) => ["b2b", "both"].includes(g(p, "customer", "customer_type")),
  },

  // 08 — Local / Visitors
  {
    id: "local_tourist_mix", step: "local_visitors", section: "business", type: "single_select",
    path: "brand_data.customer.local_tourist_mix",
    title: "Locals or visitors?",
    options: LOCAL_TOURIST_OPTIONS,
    allowUnknown: true,
  },
  {
    id: "visitor_markets", step: "local_visitors", section: "business", type: "multi_select_text",
    path: "brand_data.customer.visitor_markets",
    title: "Which visitor markets matter most?",
    help: "Press Enter to add each country / region.",
    allowUnknown: true,
    when: (p) => ["tourist", "both"].includes(g(p, "customer", "local_tourist_mix")),
  },

  // 09 — Why Customers Choose You + Proof
  {
    id: "differentiation_1", step: "why_customers_choose", section: "business",
    type: "differentiation_reason",
    path: "brand_data.differentiation.customer_choice_reasons[0]",
    title: "Why do customers choose you over the alternatives? (Reason 1)",
    options: DIFFERENTIATION_SELECTION_OPTIONS,
    allowUnknown: true,
  },
  {
    id: "differentiation_2", step: "why_customers_choose", section: "business",
    type: "differentiation_reason",
    path: "brand_data.differentiation.customer_choice_reasons[1]",
    title: "A second reason, if you have one (optional)",
    options: DIFFERENTIATION_SELECTION_OPTIONS,
    allowUnknown: true,
  },

  // 10 — Competition
  {
    id: "competitors", step: "competition", section: "business", type: "competitors",
    path: "brand_data.competition.competitors",
    title: "Your top competitors (up to 3)",
    help: "If you're not sure who they are, you can say so.",
    allowUnknown: true,
    maxEntries: 3,
  },

  // 11 — Price Reality
  {
    id: "relative_price", step: "price_reality", section: "business", type: "single_select",
    path: "brand_data.price.relative_price",
    title: "Compared to your competitors, your price is…",
    options: RELATIVE_PRICE_OPTIONS,
    allowUnknown: true,
  },

  // ========== BRAND ==========
  // 12 — Perception (six axes + never_feel as ONE logical step)
  { id: "perception_approachable_exclusive", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.approachable_exclusive",
    title: "Approachable  ⟷  Exclusive", allowUnknown: true },
  { id: "perception_classic_modern", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.classic_modern",
    title: "Classic  ⟷  Modern", allowUnknown: true },
  { id: "perception_calm_energetic", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.calm_energetic",
    title: "Calm  ⟷  Energetic", allowUnknown: true },
  { id: "perception_serious_playful", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.serious_playful",
    title: "Serious  ⟷  Playful", allowUnknown: true },
  { id: "perception_local_international", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.local_international",
    title: "Local  ⟷  International", allowUnknown: true },
  { id: "perception_warm_technical", step: "perception", section: "brand",
    type: "perception_scale",
    path: "brand_data.perception.warm_technical",
    title: "Warm  ⟷  Technical", allowUnknown: true },
  {
    id: "never_feel", step: "perception", section: "brand", type: "long_text",
    path: "brand_data.perception.never_feel",
    title: "How should your brand NEVER feel?",
    allowUnknown: true,
  },

  // 13 — Languages
  {
    id: "customer_languages", step: "languages", section: "brand", type: "multi_select",
    path: "brand_data.language.customer_languages", required: true,
    title: "Which languages do your customers speak?",
    options: CUSTOMER_LANGUAGES_OPTIONS,
  },
  {
    id: "primary_language", step: "languages", section: "brand", type: "single_select",
    path: "brand_data.language.primary_language",
    title: "Primary language for your brand",
    options: PRIMARY_LANGUAGE_OPTIONS,
    allowUnknown: true,
  },

  // 14 — Where Your Brand Will Live (applications + top-3 priorities)
  {
    id: "applications", step: "applications", section: "brand", type: "multi_select",
    path: "brand_data.applications.applications",
    title: "Where will the brand actually show up?",
    options: APPLICATIONS_OPTIONS,
    allowUnknown: true,
  },
  {
    id: "priority_applications", step: "applications", section: "brand", type: "multi_select",
    path: "brand_data.applications.priority_applications",
    title: "Pick the TOP 3 that matter most",
    options: APPLICATIONS_OPTIONS,
    allowUnknown: true,
    maxSelections: 3,
  },

  // 15 — Things to Avoid
  {
    id: "hard_constraints", step: "things_to_avoid", section: "brand", type: "multi_select_text",
    path: "brand_data.constraints.hard_constraints",
    title: "Any hard constraints we must respect?",
    help: "Legal, regulatory, cultural, technical. Press Enter per item.",
    allowUnknown: true,
  },
  {
    id: "hard_dislikes", step: "things_to_avoid", section: "brand", type: "multi_select_text",
    path: "brand_data.constraints.hard_dislikes",
    title: "Anything you absolutely do NOT want?",
    allowUnknown: true,
  },
  {
    id: "required_elements", step: "things_to_avoid", section: "brand", type: "multi_select_text",
    path: "brand_data.constraints.required_elements",
    title: "Anything that MUST be included?",
    allowUnknown: true,
  },

  // 16 — Future Growth
  {
    id: "future_growth", step: "future_growth", section: "brand", type: "multi_select",
    path: "brand_data.growth.future_growth",
    title: "What's realistic for the next 2-3 years?",
    options: FUTURE_GROWTH_OPTIONS,
    allowUnknown: true,
  },

  // ========== PROJECT ==========
  // 17 — Timing + Decision Maker (ONE concise step)
  {
    id: "deadline_type", step: "timing_decision", section: "project", type: "single_select",
    path: "brand_data.project.deadline_type",
    title: "What's your timing?",
    options: DEADLINE_TYPE_OPTIONS,
    allowUnknown: true,
  },
  {
    id: "deadline_date", step: "timing_decision", section: "project", type: "short_text",
    path: "brand_data.project.deadline_date",
    title: "A target date (if any)",
    inputType: "date",
    allowUnknown: true,
    when: (p) => g(p, "project", "deadline_type") === "fixed",
  },
  {
    id: "deadline_reason", step: "timing_decision", section: "project", type: "short_text",
    path: "brand_data.project.deadline_reason",
    title: "Anything driving that date?",
    allowUnknown: true,
    when: (p) => g(p, "project", "deadline_type") === "fixed",
  },
  {
    id: "decision_maker", step: "timing_decision", section: "project", type: "short_text",
    path: "brand_data.project.decision_maker", required: true,
    title: "Who makes the final decision on this project?",
  },
  {
    id: "feedback_contact", step: "timing_decision", section: "project", type: "short_text",
    path: "brand_data.project.feedback_contact",
    title: "Who gives feedback on drafts?",
    allowUnknown: true,
  },

  // 18 — Review What You Told Us
  { id: "review", step: "review", section: "project", type: "review",
    title: "Here's what you told us" },

  // 19 — Submit
  { id: "submit", step: "submit", section: "project", type: "submit",
    title: "Submit your intake" },
];
